import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext.jsx'
import Icon from './Icon.jsx'
import Modal from './Modal.jsx'
import TenantPicker from './TenantPicker.jsx'
import { useDismiss } from './Popover.jsx'
import { firstNameOf, initialsOf } from '../lib/text.js'

/**
 * Who you are, which company you are in, Settings, and the way out.
 *
 * Settings is here because that is where people reach for it — it is the strongest
 * convention on the web, and an argument that it is a destination rather than an account
 * matter does not buy enough to spend that. The wall view, which has no such convention
 * behind it, sits in the nav's More menu instead.
 *
 * The button itself says only a first name. The full name and the address are a line apart
 * inside, which is the right place for them, and it keeps the one control in the bar whose
 * width is set by a person's name from being the widest thing in it.
 */
export default function UserMenu() {
  const { user, tenant, otherTenants, switchTenant, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const [switching, setSwitching] = useState(false)
  const [busy, setBusy] = useState(false)
  const [switchError, setSwitchError] = useState(null)
  const ref = useRef(null)
  const navigate = useNavigate()

  async function pickTenant(tenantId) {
    setBusy(true)
    setSwitchError(null)
    try {
      await switchTenant(tenantId)
    } catch (err) {
      setSwitchError(err.message)
      setBusy(false)
    }
  }

  // The same click-away and Escape every anchored panel uses (Popover.jsx). The panel is
  // rendered inside `ref` rather than portalled, so the anchor and the panel are one element.
  const close = useCallback(() => setOpen(false), [])
  useDismiss(open, close, ref, ref)

  const go = (path) => { setOpen(false); navigate(path) }

  return (
    <div className="user-menu" ref={ref}>
      <button
        className={`user-button ${open ? 'open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="avatar">{initialsOf(user?.name)}</span>
        <span className="user-name">{firstNameOf(user?.name)}</span>
        <span className="chevron" aria-hidden="true">
          <Icon name="down" size={13} strokeWidth={2.2} />
        </span>
      </button>

      {open && (
        <div className="menu-panel" role="menu">
          <div className="menu-identity">
            <div className="menu-name">{user?.name}</div>
            <div className="menu-email">{user?.email}</div>
            {/* The company, not the roles. This menu answers "who am I signed in as", and for
                somebody who belongs to more than one company that question is half about
                WHICH company — which the switcher below then acts on. A row of role badges
                was a permissions readout on a menu nobody opens to check permissions;
                Settings → Account lists those in full. */}
            <div className="menu-tenant">
              {tenant?.logoPath && (
                <img src={`/uploads/${tenant.logoPath}`} alt="" className="menu-tenant-mark" />
              )}
              <span>{tenant?.name}</span>
            </div>
          </div>

          {/* Everyone gets Settings — Account lives there for every role, and the Company /
              Owner groups inside it appear or vanish on their own depending on can.administer
              / can.isOwner. One door, not a different one per permission level.

              Under the avatar rather than in the nav, which is where people reach for it:
              near enough every application on the web puts it here, and a convention that
              strong is not worth spending to make a tidier diagram of the header. */}
          <div className="menu-section">
            <button role="menuitem" onClick={() => go('/settings')}>
              Settings
            </button>
            {/* Only worth offering when this identity actually has somewhere else to go — a
                dev-signed-in account or a single-company identity never sees it. */}
            {otherTenants.length > 0 && (
              <button
                role="menuitem"
                onClick={() => { setOpen(false); setSwitchError(null); setSwitching(true) }}
              >
                Switch account
              </button>
            )}
          </div>

          <div className="menu-section">
            <button role="menuitem" className="danger" onClick={signOut}>
              Sign out
            </button>
          </div>
        </div>
      )}

      {switching && (
        <Modal title="Switch account" onClose={() => setSwitching(false)} width={380}>
          <p className="sub">Choose which company to switch to.</p>
          {switchError && <div className="error">{switchError}</div>}
          <TenantPicker tenants={otherTenants} busy={busy} onPick={pickTenant} current={tenant} />
        </Modal>
      )}
    </div>
  )
}
